import EventEmitter from 'event-emitter-es6'

import History from './history'
import Modifier from './modifier'
import Field from '../mechanism/field'
import Type from '../mechanism/type'

export default class Character extends EventEmitter {
  constructor (playbook) {
    super()
    Object.assign(this, {
      playbook,
      rulebooks: playbook.rulebooks,
      choices: {},
      modifiers: [],
      fields: {},
      playbookFields: {}, // A calculated version of this.playbook.fields
      notes: '',
      history: new History(),
      alive: true
    })
    this.history.mount(this)
    this._refresh()
  }

  // Calculating all fields after character creation, registering all hooks
  async initialize (ui) {
    this.emit('init:start')
    for(const field of this.playbook.rules.characterFields.filter(field => field instanceof Field.ManualField)) {
      this.fields[field.name] = await this.get(field.initializationFormula, { ui })
    }

    for (const hook of (this.playbook.rules.hooks || [])) {
      this.on(hook.trigger, () => {
        this.emit('hook:start', hook)
        this.execute(hook.procedure, { ui })
        this.emit('hook:over', hook)
      })
    }
    await this._refresh()
    this.history.clear()
    this.emit('init:over')
  }

  // Field procedures
  async die () {
    this.alive = false
    this.emit('death')
  }

  async revive () {
    this.alive = true
    this.emit('revival')
  }

  async set (field, value) {
    await this._change(() => {
      this.fields[field] = value
    })
  }

  async addToField (field, value) {
    await this._change(() => {
      if (Array.isArray(this.fields[field])) {
        this.fields[field].push(value)
      } else {
        this.fields[field] += Number(value)
      }
    })
  }

  async removeFromField (field, value) {
    await this._change(() => {
      if (Array.isArray(this.fields[field])) {
        this.fields[field].value.splice(field.value.indexOf(value))
      } else {
        this.fields[field] -= Number(value)
      }
    })
  }

  async toggle (field) {
    await this._change(() => {
      this.fields[field] = !this.fields[field]
    })
  }

  async addModifier (modifierParams) {
    await this._change(() => {
      this.modifiers.push(new Modifier(modifierParams))
    })
  }

  async _change (callback) {
    this.emit('change:start')
    await callback()
    this.emit('change:refreshing')
    await this._refresh()
    this.emit('change:over')
  }

  async _refresh () {
    for(const field of this.playbook.rules.characterFields.filter(field => field instanceof Field.AutomaticField)) {
      this.fields[field.name] = await this.get(field.calculationFormula, {})
    }
    for(const field of this.playbook.rules.playbookFields) {
      if (field.type.name === 'formula') {
        this.playbookFields[`${field.name}-formula`] = this.playbook.fields[field.name]
      }
      this.playbookFields[field.name] = await this.get(field.name, {})
    }
  }

  // Calculations and Executions
  async get (raw, { ui, type = undefined, context = { } }) {
    this.emit('get:start', { raw, type, context })
    if (type === 'field' || (raw.constructor !== String && Object.keys(raw).length > 1)) return raw
    if (Array.isArray(raw)) {
      const result = []
      for (const item in raw) result.push(await this.get(item, { ui, context }))
      return result
    }
    const modifierValue = await Modifier.calculate(this.modifiers, { raw, character: this, ui, type, context })
    const rawValue = await this.getRawValue({ raw, ui, type, context, fallback: raw })
    // TODO Handle special cases of addition like arrays and numbers according to the type
    const value = rawValue // + modifierValue
    this.emit('get:over', { raw, value, type, context, rawValue, modifierValue })
    return value
  }

  async execute (raw='do nothing', ui, context = { }) {
    this.emit('execution:start', { raw, ui, context })
    if (Array.isArray(raw)) {
      for (const item of raw)
        await this.execute(item, ui, context)
      return
    }

    const procedure = await this.getRawValue({ raw, ui, type: 'procedure', context, fallback: async () => await ui.output(raw) })
    await procedure({ character: this, raw, ui, context })
    this.emit('execution:over', { raw, ui, context, value: procedure })
  }

  async getRawValue ({ raw, ui, type, context={}, fallback }) {
    if(context.hasOwnProperty(raw)) return context[raw]
    const matchingValuables = this.playbook.valuables.filter(v => v.match(raw, type))

    let value
    valuesearch: for (const valuable of matchingValuables) {
      value = await valuable?.getValue({ character: this, raw, ui, type, context })
      if (value)  break valuesearch
    }
    const result = this.getValueFromType((value === undefined ? raw : value), type)
    return (result === undefined) ? fallback : result
  }

  getValueFromType (raw, type) {
    if (!type || type === 'any')  return this.automaticlyType(raw)


    if (type.constructor === String) {
      if (type === 'array') return raw
      type = this.getType(type)
    }

    if (!type)  return raw

    return type.parseValue(raw)
  }

  automaticlyType (raw) {
    if (Array.isArray(raw))                 return raw.map(item => this.getValueFromType(item))
    if (raw.constructor !== String)         return raw
    if (raw.match(/^\[.*\]$/))              return raw.substring(1, raw.length - 1).split(',').map(x => x.trim()).filter(x => x.length > 0)
    if (['true', 'yes'].includes(raw))      return true
    if (['false', 'no'].includes(raw))      return false
    if (isFinite(raw))                      return Number(raw)
    return raw
  }

  getType (name) {
    if (name.endsWith('array')) {
      const childType = this.getType(name.replace(/array$/).trim())
      return Type.Array(childType)
    }
    return this.playbook.rules.types.find(typeDefinition => typeDefinition.name === name)
  }

  // Serialization
  toJson () {
    return Character.serialize(this)
  }

  static deserialize (json, rulebook) {
    const playbook = rulebook.playbooks.find(playbook => playbook.name === json.playbook)
    const character = new Character(playbook)

    Object.assign(character, {
      choices: json.choices,
      fields: json.fields,
      alive: json.alive,
      modifiers: json.modifiers.map(modifier => new Modifier(modifier)),
      notes: json.notes,
      history: History.deserialize(json.history)
    })

    return character
  }

  static serialize (character) {
    return {
      rulebooks: character.rulebooks,
      playbook: character.playbook.name,
      choices: character.choices,
      fields: character.fields,
      alive: character.alive,
      modifiers: character.modifiers.map(modifier => modifier.toJson()),
      notes: character.notes,
      history: character.history.toJson()
    }
  }
}

/*
* About UI:
* The UI parameter is an interface with the user and has the following functions:
* *
* * async output(text, title)             shows a text to the user.
* *                                       resolves when the user closes the window.
* *
* * async input(text, type=string)        shows a text to the user and accepts an input in response.
* *                                       resolves with the value from the user.
* *
* * async choose(text, options, count=1)  shows a text to the user and accepts a choice.
* *                                       resolves with the chosen value.
*/
